import { faClock } from '@skeepers/skeepers-icons/regular/faClock';
import { faMobile } from '@skeepers/skeepers-icons/regular/faMobile';
import { faChevronRight } from '@skeepers/skeepers-icons/solid/faChevronRight';
import { faVolumeMute } from '@skeepers/skeepers-icons/solid/faVolumeMute';
import { SkpButton, SkpFlex, SkpIcon, SkpTag, SkpText } from '@skeepers/skeepers-ui-react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import formatDuration from '@app/utils/formatDuration';
import { getTranslationForSequenceType } from '@app/utils/sequence';
import Block from '@components/Common/Block';
import BodyContainer from '@components/Common/BodyContainer';
import Header from '@components/Common/Header';
import PreviousButton from '@components/Common/PreviousButton';

import style from './style.module.css';

const BriefPage = ({
  index,
  title,
  description,
  orientation,
  type,
  durationMin,
  durationMax,
  quantity,
  mute,
  optional,
  handleReturn,
  handleUpload,
  handleSkip,
  isModal = false,
  onCapture,
  instructions,
  reviewMessage = null,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {handleReturn && <PreviousButton onClick={handleReturn} />}
      <BodyContainer className={classNames(style.bodyContainer, { [style.modal]: isModal })}>
        <Header title={t('Step {{index}}', { index })} subtitle={title} />
        {optional && <div className={classNames(style.optionalSequenceBlock, 'd-flex flex-column flex-2 align-center flex-tb-row')}>
          <SkpTag type="ghost" text={t('Optional step')} color='var(--skp-color-black)' className={style.optionalTag} />
          <SkpText text={t('Include this step if it is relevant to your product testing')} color='var(--skp-color-neutral-700)' size="small" />
        </div>}
        <section className={style.section}>
          {
            reviewMessage && (
              <div data-testid='review-message' className={style.reviewMessage}>
                {t('This step needs to be redone for the following reason : {{reason}}', {reason: reviewMessage.message})}
              </div>
            )
          }
          <div data-testid='description-container' className={style.description}>
            {description.split('\n').map((d, index) => (
              <p key={`${index}-${d}`} data-testid='description' className={style.description}>
                {d}
              </p>
            ))}
          </div>
          <br />
          <div className={classNames('d-flex align-center', style.requirementContainer)}>
            {!!durationMin && !!durationMax && (
              <SkpFlex dataTestid='timming-container' flexDirection='column' alignItems='center' justifyContent='center' gap={2}>
                <SkpIcon icon={faClock} color='--skp-color-neutral-600' size='large'/>
                <SkpText text={`${formatDuration(durationMin, t)} - ${formatDuration(durationMax, t)}`} size="small" bold color='--skp-color-neutral-600' />
              </SkpFlex>
            )}
            {orientation && (
              <SkpFlex dataTestid='orientation-container' flexDirection='column' alignItems='center' justifyContent='center' gap={2}>
                <SkpIcon icon={faMobile} color='--skp-color-neutral-600' size='large'  rotate={orientation === 'HORIZONTAL' ? 270 : 0}/>
                <SkpText text={orientation === 'VERTICAL' ? t('Vertical') : t('Horizontal')} size="small" bold color='--skp-color-neutral-600' />
              </SkpFlex>
            )}
            {mute && (
              <SkpFlex dataTestid='mute-container' flexDirection='column'  alignItems='center' justifyContent='center' gap={2}>
                <SkpIcon icon={faVolumeMute} color='--skp-color-neutral-600' size='large'/>
                <SkpText
                  text={t('Sound not recorded')}
                  color='--skp-color-neutral-600'
                  size='small'
                  bold
                />
              </SkpFlex>
            )}
          </div>
          <br />
          {quantity && (
            <div data-testid='quantity-container' className={style.infoContainer}>
              <SkpText text={t('Number of pictures: ')} size="small" bold color="--skp-color-neutral-500" />
              <SkpText text={t('{{count}} picture', { count: quantity })} size="small" bold color="#3271f5" />
            </div>
          )}
          {instructions?.length > 0 && (
            <Block className={style.blockRoot}>
              <h2 data-testid='instructions-title'>{t('Follow the guidelines')}</h2>
              <div data-testid='instructions-block' className={style.tips}>
                {instructions.map((tip, idx) => (
                  <p data-testid='instruction' key={idx} className={style.tip}>
                    {tip}
                  </p>
                ))}
              </div>
            </Block>
          )}
          {onCapture && (
            <div data-testid='buttons-container' className={style.buttonContainer}>
              {optional && (
                <button
                  className={style.skipButtonWrapper}
                  onClick={handleSkip}
                  type="button"
                >
                  <SkpText text={t('Skip this step')} bold />
                  <SkpIcon
                    icon={faChevronRight}
                    className={style.skipIcon}
                  />
                </button>
              )}
              <div className={style.mainButtons}>
                <SkpButton text={t('Import')} onSkpClick={handleUpload} width="full" skpClass={style.button} />
                <SkpButton type="outline" text={t(`Record my ${getTranslationForSequenceType(type)} (beta)`)} onSkpClick={onCapture} width="full" skpClass={style.button} />
              </div>
            </div>
          )}
        </section>
      </BodyContainer>
    </>
  );
};

export default BriefPage;

BriefPage.propTypes = {
  handleReturn: PropTypes.func,
  handleMenu: PropTypes.func,
  handleSkip: PropTypes.func,
  handleUpload: PropTypes.func,
  onCapture: PropTypes.func,
  optional: PropTypes.bool,
};
